import React, { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Divider } from "@evergis/ui";

import { DataSourceInnerContainer } from "./DataSourceInnerContainer";
import {
  DataSourceProgressContainerWrapper,
  ProgressTotal,
  ProgressTotalTitle,
  ProgressUnits,
  ProgressValue,
} from "./styled";
import { LegendToggler } from "../../LayerList/styled";
import { ExpandableTitle } from "../components/ExpandableTitle/ExpandableTitle";
import { HiddenTitleItems } from "./HiddenTitleItems";
import { ContainerLoading } from "../components/Loading/ContainerLoading";
import { DataSourceError } from "../components/DataSourceError/DataSourceError";
import { formatAttributeValue } from "../../../utils/formatAttributeValue";
import { isVisibleContainer } from "../utils/isVisibleContainer";
import { useRelatedDataSourceAttributes } from "../hooks/useRelatedDataSourceAttributes";
import { useWidgetContext } from "../../Dashboard/hooks/useWidgetContext";
import { useShownOtherItems } from "../hooks/useShownOtherItems";
import { useLoadingData } from "../hooks/useLoadingData";
import { ContainerProps } from "../types";
import { LayerAttribute } from "../../../components/Dialogs/EditResource/components/Layer/LayerAttributeItem";

export const DataSourceProgressContainer: FC<ContainerProps> = memo(
  ({ config, elementConfig, type, renderElement }) => {
    const { t } = useTranslation("featureCard");
    const { dataSources, expandedContainers } = useWidgetContext(type);
    const { dataSource, layerInfo } = useRelatedDataSourceAttributes({ config, elementConfig, dataSources });
    const features = useLoadingData(dataSource?.features);

    const { attributes } = layerInfo?.configuration?.attributesConfiguration || {};

    const { id, options, children } = elementConfig || {};
    const { maxValue, showTotal, relatedDataSource, expandable, expanded } = options || {};
    const valueElement = children?.find(item => item.id === "value");
    const unitsElement = children?.find(item => item.id === "units");

    const { sliceItems, checkIsSliced, showMore, onShowMore } = useShownOtherItems(options);

    const totalUnits = useMemo(
      () =>
        unitsElement?.type === "attributeUnits"
          ? attributes?.find(({ attributeName }) => attributeName === unitsElement?.attributeName)?.stringFormat
              ?.unitsLabel
          : features?.[0]?.attributes[unitsElement?.attributeName as string],
      [attributes, features, unitsElement?.attributeName, unitsElement?.type],
    );

    const totalValue = useMemo(() => {
      const attribute = attributes?.find(({ attributeName }) => attributeName === valueElement?.attributeName);
      const { type: attributeType, stringFormat } = (attribute as LayerAttribute) || {};

      const result = features?.reduce(
        (total, feature) => total + feature.attributes[valueElement?.attributeName as string],
        0,
      );

      return formatAttributeValue(attributeType, result, stringFormat, !!unitsElement?.type);
    }, [attributes, features, unitsElement?.type, valueElement?.attributeName]);

    const currentMaxValue = useMemo(() => {
      if (typeof maxValue === "string") {
        return features?.[0]?.attributes[maxValue];
      }

      return (
        maxValue ||
        features?.reduce((result, item) => Math.max(result, item.attributes[valueElement?.attributeName as string]), 0)
      );
    }, [features, maxValue, valueElement?.attributeName]);

    if (!relatedDataSource) return null;

    if (dataSource && !features) {
      return <DataSourceError name={elementConfig.templateName} />;
    }

    const isVisible = isVisibleContainer(id, expandable, expanded, expandedContainers);
    const isLoading = !dataSource?.features;

    return (
      <>
        <ExpandableTitle elementConfig={elementConfig} type={type} renderElement={renderElement} />
        {!isVisible ? null : (
          <div style={{ position: "relative" }}>
            {isLoading && <ContainerLoading />}
            <DataSourceProgressContainerWrapper isLoading={isLoading}>
              {sliceItems(features)?.map((feature, index) => (
                <DataSourceInnerContainer
                  key={index}
                  index={index}
                  feature={feature}
                  config={config}
                  elementConfig={elementConfig}
                  maxValue={currentMaxValue}
                  type={type}
                />
              ))}
              {checkIsSliced(features) && (
                <LegendToggler toggled={showMore} onClick={onShowMore}>
                  {showMore ? t("hide") : t("showAll")}
                </LegendToggler>
              )}
              {showTotal && (
                <>
                  <Divider />
                  <ProgressTotal>
                    <ProgressTotalTitle>{t("total")}</ProgressTotalTitle>
                    <ProgressValue>
                      {totalValue}
                      <ProgressUnits>{totalUnits}</ProgressUnits>
                    </ProgressValue>
                  </ProgressTotal>
                </>
              )}
            </DataSourceProgressContainerWrapper>
          </div>
        )}
        <HiddenTitleItems elementConfig={elementConfig} config={config} type={type} />
      </>
    );
  },
);
